import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Typography,
  Row,
  Col,
  Tag,
  DatePicker,
  Tabs,
  Select,
  Descriptions,
  Badge,
  Dropdown,
  Statistic,
  Divider,
  Tooltip,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  GiftOutlined,
  UserDeleteOutlined,
  BarChartOutlined,
  SearchOutlined,
  DownloadOutlined,
  HistoryOutlined,
  UserOutlined,
  SettingOutlined,
  CrownOutlined,
  BranchesOutlined,
  RollbackOutlined,
  DollarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { RewardsEditor } from '@/components/RewardsEditor';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import { pickForConfigVersion, isVariantRow } from '@/utils/configVersionScope';
import { DEFAULT_MAX_PLAYER_INDEX } from '@/constants/playerData';
import { formatMoney } from '@utils/formatMoney';
import type {
  DailyDealConfig,
  DailyDealAnalytics,
  DealType,
  DailyDealPurchase,
  UserDailyDealsStatus,
  UserDealStatus,
  DealRewards,
  DealPricing,
  PurchaseHistoryFilters,
  PaymentMethod,
} from '@/types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export const DailyDealsPage: React.FC = () => {
  // Config state
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<DailyDealConfig[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<DailyDealConfig | null>(null);
  const [editingRewards, setEditingRewards] = useState<DealRewards>({});
  const [editingPricing, setEditingPricing] = useState<DealPricing>({});
  const [resetCooldownModalVisible, setResetCooldownModalVisible] = useState(false);
  const [regenerateSeedModalVisible, setRegenerateSeedModalVisible] = useState(false);
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false);
  const [analytics, setAnalytics] = useState<DailyDealAnalytics[]>([]);
  const [editForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [regenerateForm] = Form.useForm();
  const [analyticsForm] = Form.useForm();

  // Purchase History state
  const [purchases, setPurchases] = useState<DailyDealPurchase[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesPageSize, setPurchasesPageSize] = useState(20);
  const [purchasesFilters, setPurchasesFilters] = useState<PurchaseHistoryFilters>({});
  const [purchaseFilterForm] = Form.useForm();

  // Live max playerIndex for the Featured Player input. Falls back to the
  // shared DEFAULT_MAX_PLAYER_INDEX constant (current production max) until
  // the roster-size endpoint responds.
  const [maxPlayerIndex, setMaxPlayerIndex] = useState<number>(DEFAULT_MAX_PLAYER_INDEX);
  useEffect(() => {
    adminApi
      .getRosterSize()
      .then((res) => setMaxPlayerIndex(res.data.maxPlayerIndex))
      .catch(() => {
        /* keep fallback */
      });
  }, []);

  // User Lookup state
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const [userStatus, setUserStatus] = useState<UserDailyDealsStatus | null>(null);
  const [userLookupForm] = Form.useForm();

  // Which build's deals are being viewed. Null is the shared set every build
  // inherits — the default, and what the page showed before per-build config
  // existed. Picking a version shows that build's resolved set instead.
  const [scopeConfigVersionId, setScopeConfigVersionId] = useState<string | null>(null);

  // Every row, shared and variant alike. The resolved view is derived from this
  // rather than re-fetched, so switching scope is instant and so a variant can
  // be compared against the shared row it overrides.
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getDailyDealsConfigs();
      setConfigs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch daily deals configs', error);
      message.error('Failed to load daily deals configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPurchases = useCallback(
    async (page = 1, filters = purchasesFilters, size?: number) => {
      const limit = size || purchasesPageSize;
      setPurchasesLoading(true);
      try {
        const res = await adminApi.getDailyDealPurchases({
          ...filters,
          page,
          limit,
        });
        setPurchases(res.data.data || []);
        setPurchasesTotal(res.data.total || 0);
        setPurchasesPage(page);
      } catch (error) {
        console.error('Failed to fetch purchases', error);
        message.error('Failed to load purchase history');
      } finally {
        setPurchasesLoading(false);
      }
    },
    [purchasesFilters, purchasesPageSize]
  );

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleToggle = async (
    dealType: string,
    currentStatus: boolean,
    configVersionId?: string | null
  ) => {
    try {
      // Pass the scope of the row actually shown, not the selected scope: a
      // build inheriting the shared row must toggle the shared row.
      await adminApi.toggleDailyDeal(dealType, configVersionId);
      message.success(`${dealType} ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchConfigs();
    } catch (error) {
      console.error('Failed to toggle deal', error);
      message.error('Failed to toggle deal status');
    }
  };

  const handleEdit = (record: DailyDealConfig) => {
    setEditingConfig(record);
    setEditingRewards(record.rewards || {});
    setEditingPricing(record.pricing || {});
    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      gemCost: record.gemCost != null ? Number(record.gemCost) : null,
      iapCostUsd: record.iapCostUsd != null ? Number(record.iapCostUsd) : null,
      iapProductId: record.iapProductId,
      cooldownHours: record.cooldownHours,
      sortOrder: record.sortOrder,
      featuredPlayerIndex: record.featuredPlayerIndex,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values: Record<string, unknown>) => {
    if (!editingConfig) return;

    try {
      await adminApi.updateDailyDealConfig(
        editingConfig.dealType,
        {
          ...values,
          pricing: editingPricing,
          rewards: editingRewards,
          featuredPlayerIndex: (values.featuredPlayerIndex as number) ?? undefined,
        },
        // Edit the row on screen. Editing an inherited shared row while a
        // config version is selected would change every other build too, so
        // the UI offers "Override for this build" for that case instead.
        editingConfig.configVersionId
      );
      message.success('Configuration updated successfully');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingConfig(null);
      setEditingRewards({});
      setEditingPricing({});
      fetchConfigs();
    } catch (error) {
      console.error('Failed to update config', error);
      message.error('Failed to update configuration');
    }
  };

  const handleResetCooldown = async (values: { userId: string; dealType: DealType }) => {
    try {
      await adminApi.resetUserCooldown(values);
      message.success('User cooldown reset successfully');
      setResetCooldownModalVisible(false);
      resetForm.resetFields();
      // Refresh user status if viewing same user
      if (userStatus?.userId === values.userId) {
        handleUserLookup({ userId: values.userId });
      }
    } catch (error) {
      console.error('Failed to reset cooldown', error);
      message.error('Failed to reset user cooldown');
    }
  };

  const handleRegenerateSeed = async (values: { date?: dayjs.Dayjs }) => {
    try {
      const payload = values.date ? { date: dayjs(values.date).format('YYYY-MM-DD') } : {};
      const res = await adminApi.regenerateDailySeed(payload);
      message.success(
        `Seed regenerated: ${res.data.dropResult?.type} x${res.data.dropResult?.baseAmount}`
      );
      setRegenerateSeedModalVisible(false);
      regenerateForm.resetFields();
    } catch (error) {
      console.error('Failed to regenerate seed', error);
      message.error('Failed to regenerate seed');
    }
  };

  const handleShowAnalytics = async (values?: {
    startDate?: dayjs.Dayjs;
    endDate?: dayjs.Dayjs;
    dealType?: string;
  }) => {
    try {
      const params: Record<string, string> = {};
      if (values?.startDate) {
        params.startDate = dayjs(values.startDate).format('YYYY-MM-DD');
      }
      if (values?.endDate) {
        params.endDate = dayjs(values.endDate).format('YYYY-MM-DD');
      }
      if (values?.dealType) {
        params.dealType = values.dealType;
      }

      const res = await adminApi.getDailyDealsAnalytics(params);
      setAnalytics(res.data || []);
      setAnalyticsModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
      message.error('Failed to load analytics');
    }
  };

  const handlePurchaseFilter = (values: {
    userId?: string;
    userName?: string;
    dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
    dealType?: DealType;
    paymentMethod?: PaymentMethod;
  }) => {
    const filters: typeof purchasesFilters = {};
    if (values.userId) filters.userId = values.userId;
    if (values.userName) filters.userName = values.userName;
    if (values.dateRange?.[0]) filters.startDate = dayjs(values.dateRange[0]).format('YYYY-MM-DD');
    if (values.dateRange?.[1]) filters.endDate = dayjs(values.dateRange[1]).format('YYYY-MM-DD');
    if (values.dealType) filters.dealType = values.dealType;
    setPurchasesFilters(filters);
    fetchPurchases(1, filters);
  };

  const handleUserLookup = async (values: { userId: string }) => {
    if (!values.userId?.trim()) {
      message.warning('Please enter a user ID');
      return;
    }
    setUserLookupLoading(true);
    try {
      const res = await adminApi.getUserDailyDealsStatus(values.userId.trim());
      setUserStatus(res.data);
    } catch (error: unknown) {
      console.error('Failed to lookup user', error);
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        message.error('User not found');
      } else {
        message.error('Failed to lookup user');
      }
      setUserStatus(null);
    } finally {
      setUserLookupLoading(false);
    }
  };

  const handleExport = async (type: 'purchases' | 'analytics') => {
    try {
      const res =
        type === 'purchases'
          ? await adminApi.exportDailyDealPurchases(purchasesFilters)
          : await adminApi.exportDailyDealAnalytics({});

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_deals_${type}_${dayjs().format('YYYY-MM-DD')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('Export downloaded successfully');
    } catch (error) {
      console.error('Failed to export', error);
      message.error('Failed to export data');
    }
  };

  const exportMenuItems: MenuProps['items'] = [
    { key: 'purchases', label: 'Export Purchase History', icon: <HistoryOutlined /> },
    { key: 'analytics', label: 'Export Analytics', icon: <BarChartOutlined /> },
  ];

  const formatPricing = (config: DailyDealConfig) => {
    const parts: string[] = [];
    if (config.pricing?.coins) parts.push(`${config.pricing.coins} Coins`);
    if (config.pricing?.matchTokens) parts.push(`${config.pricing.matchTokens} MT`);
    if (config.pricing?.evoTokens) parts.push(`${config.pricing.evoTokens} ET`);
    if (config.pricing?.premiumCurrency) parts.push(`${config.pricing.premiumCurrency} Gems`);
    else if (config.gemCost) parts.push(`${config.gemCost} Gems`);
    if (config.iapCostUsd) parts.push(`$${Number(config.iapCostUsd).toFixed(2)}`);
    return parts.length > 0 ? parts.join(' / ') : 'Free';
  };

  // What a client on the selected config version is actually served: its own
  // variant per deal type where one exists, else the shared row. Mirrors the
  // backend's pickForConfigVersion so the panel and the game agree.
  const resolvedConfigs = React.useMemo(
    () => pickForConfigVersion(configs, scopeConfigVersionId, (c) => c.dealType),
    [configs, scopeConfigVersionId]
  );

  // Fork the shared row so this build can diverge. The variant starts as a copy,
  // so nothing changes for players until it is edited.
  const handleCreateOverride = async (record: DailyDealConfig) => {
    if (!scopeConfigVersionId) return;
    try {
      await adminApi.createDailyDealVariant(record.dealType, scopeConfigVersionId);
      message.success(`${record.dealType} now has its own row for this config version`);
      fetchConfigs();
    } catch (error) {
      console.error('Failed to create deal variant', error);
      message.error('Failed to create override');
    }
  };

  // Drop the override; builds on this version inherit the shared row again.
  const handleRemoveOverride = (record: DailyDealConfig) => {
    if (!record.configVersionId) return;
    const configVersionId = record.configVersionId;
    Modal.confirm({
      title: `Remove the ${record.dealType} override?`,
      content:
        'Builds on this config version will go back to the shared row. The ' +
        'override and its edits are deleted.',
      okText: 'Remove override',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await adminApi.deleteDailyDealVariant(record.dealType, configVersionId);
          message.success('Override removed — this build inherits the shared row again');
          fetchConfigs();
        } catch (error) {
          console.error('Failed to delete deal variant', error);
          message.error('Failed to remove override');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Deal Type',
      dataIndex: 'dealType',
      key: 'dealType',
      render: (text: string) => <Tag color="blue">{text.replace(/_/g, ' ').toUpperCase()}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Pricing',
      key: 'pricing',
      render: (_: unknown, record: DailyDealConfig) => formatPricing(record),
    },
    {
      title: 'Cooldown',
      dataIndex: 'cooldownHours',
      key: 'cooldownHours',
      render: (hours: number) => `${hours}h`,
    },
    {
      title: 'Featured Player',
      key: 'featuredPlayerIndex',
      render: (_: unknown, record: DailyDealConfig) =>
        record.featuredPlayerIndex != null ? (
          <Tag color="orange">Player #{record.featuredPlayerIndex}</Tag>
        ) : (
          <Text type="secondary">Auto</Text>
        ),
    },
    {
      title: 'Scope',
      key: 'scope',
      render: (_: unknown, record: DailyDealConfig) =>
        isVariantRow(record, scopeConfigVersionId) ? (
          <Tooltip title="This build has its own row. Editing it affects only builds on this config version.">
            <Tag color="purple">Override</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Inherited from the row every build shares. Editing it affects all builds.">
            <Tag>Shared</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: DailyDealConfig) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggle(record.dealType, isActive, record.configVersionId)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      // Wide enough for Edit plus the override action; without a floor the
      // second button is clipped and unclickable.
      width: 220,
      render: (_: unknown, record: DailyDealConfig) => {
        const isOverride = isVariantRow(record, scopeConfigVersionId);
        return (
          <Space size={0}>
            <Tooltip
              title={
                isOverride
                  ? 'Edit this build\u2019s copy'
                  : scopeConfigVersionId
                    ? 'Edits the shared row \u2014 every build is affected'
                    : undefined
              }
            >
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                Edit
              </Button>
            </Tooltip>
            {scopeConfigVersionId &&
              (isOverride ? (
                <Button
                  type="link"
                  danger
                  icon={<RollbackOutlined />}
                  onClick={() => handleRemoveOverride(record)}
                >
                  Revert
                </Button>
              ) : (
                <Tooltip title="Copy this row so this config version can diverge without touching other builds.">
                  <Button
                    type="link"
                    icon={<BranchesOutlined />}
                    onClick={() => handleCreateOverride(record)}
                  >
                    Override
                  </Button>
                </Tooltip>
              ))}
          </Space>
        );
      },
    },
  ];

  const formatCurrencyType = (type: string | null | undefined) => {
    const labels: Record<string, string> = {
      coins: 'Coins',
      matchTokens: 'Match Tokens',
      evoTokens: 'Evo Tokens',
      premiumCurrency: 'Gems',
    };
    return type ? labels[type] || type : '-';
  };

  const RARITY_LABELS = ['Common', 'Semi-Pro', 'Pro', 'Legendary'];
  const RARITY_BORDER: Record<number, string> = {
    0: '#52c41a',
    1: '#13c2c2',
    2: '#eb2f96',
    3: '#722ed1',
  };
  const RARITY_TAG_COLORS: Record<number, string> = {
    0: 'green',
    1: 'cyan',
    2: 'magenta',
    3: 'purple',
  };
  const PLAYER_TYPE_LABELS = ['Batsman', 'Bowler', 'All-Rounder'];

  const renderRewards = (rewards: Record<string, unknown>) => {
    if (!rewards || Object.keys(rewards).length === 0) return '-';
    const items: React.ReactNode[] = [];

    // Currency rewards
    if (rewards.coins)
      items.push(
        <Tag key="coins" color="gold">
          +{Number(rewards.coins).toLocaleString()} Coins
        </Tag>
      );
    if (rewards.matchTokens)
      items.push(
        <Tag key="mt" color="purple">
          +{String(rewards.matchTokens)} MT
        </Tag>
      );
    if (rewards.evoTokens)
      items.push(
        <Tag key="et" color="cyan">
          +{String(rewards.evoTokens)} ET
        </Tag>
      );
    if (rewards.premiumCurrency)
      items.push(
        <Tag key="gems" color="magenta">
          +{String(rewards.premiumCurrency)} Gems
        </Tag>
      );

    // Card rewards
    const cards = rewards.cards as
      | {
          count?: number;
          rarity?: number;
          grantedCards?: Array<Record<string, unknown>>;
        }
      | undefined;

    if (cards?.grantedCards?.length) {
      cards.grantedCards.forEach((card, i) => {
        const rarity = cards.rarity ?? 0;
        const rarityLabel = RARITY_LABELS[rarity] || 'Unknown';
        const playerType = PLAYER_TYPE_LABELS[Number(card.playerType)] || '';
        const rating = Number(card.playerRating);

        items.push(
          <div
            key={`card-${i}`}
            style={{
              borderLeft: `3px solid ${RARITY_BORDER[rarity] || '#d9d9d9'}`,
              padding: '2px 6px',
              background: '#fafafa',
              borderRadius: 4,
              marginTop: 2,
              display: 'inline-block',
            }}
          >
            <Text strong style={{ fontSize: 11 }}>
              {String(card.firstName)} {String(card.secondName)}
            </Text>{' '}
            <Tag
              color={RARITY_TAG_COLORS[rarity]}
              style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px' }}
            >
              {rarityLabel}
            </Tag>
            <Tag style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>{rating}</Tag>
            <div style={{ fontSize: 10, color: '#888' }}>
              {String(card.teamName)} · {playerType} · #{String(card.jerseyNumber)}
            </div>
          </div>
        );
      });
    } else if (rewards.cardCount) {
      const rarity = rewards.cardRarity != null ? Number(rewards.cardRarity) : 0;
      items.push(
        <Tag key="cards" color={RARITY_TAG_COLORS[rarity]}>
          {String(rewards.cardCount)} {RARITY_LABELS[rarity] || ''} Card(s)
        </Tag>
      );
    }

    if (rewards.playerIndex != null && !cards?.grantedCards?.length)
      items.push(
        <Tag key="player" color="green">
          Player #{String(rewards.playerIndex)}
        </Tag>
      );

    return items.length > 0 ? (
      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>{items}</div>
    ) : (
      '-'
    );
  };

  const PAYMENT_COLORS: Record<string, string> = {
    free: 'green',
    coins: 'gold',
    matchTokens: 'cyan',
    evoTokens: 'blue',
    premiumCurrency: 'purple',
    iap: 'magenta',
  };

  const DEAL_TYPE_COLORS: Record<string, string> = {
    free_drop: 'green',
    free_coin_drop: 'gold',
    free_card_drop: 'cyan',
    player_of_day: 'blue',
    legend_of_week: 'purple',
  };

  const COST_COLORS: Record<string, string> = {
    coins: '#faad14',
    matchTokens: '#722ed1',
    evoTokens: '#13c2c2',
    premiumCurrency: '#eb2f96',
  };

  const purchaseColumns = [
    {
      title: 'User',
      key: 'user',
      width: 180,
      sorter: (a: DailyDealPurchase, b: DailyDealPurchase) =>
        (a.userDisplayName || '').localeCompare(b.userDisplayName || ''),
      render: (_: unknown, record: DailyDealPurchase) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 12 }}>
            {record.userDisplayName || 'Unknown'}
          </Text>
          <Tooltip title={record.userId}>
            <Text type="secondary" copyable={{ text: record.userId }} style={{ fontSize: 10 }}>
              {record.userId.slice(0, 8)}...
            </Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Deal',
      dataIndex: 'dealType',
      key: 'dealType',
      width: 130,
      sorter: (a: DailyDealPurchase, b: DailyDealPurchase) => a.dealType.localeCompare(b.dealType),
      filters: [
        { text: 'Free Drop', value: 'free_drop' },
        { text: 'Free Coin Drop', value: 'free_coin_drop' },
        { text: 'Free Card Drop', value: 'free_card_drop' },
        { text: 'Player of Day', value: 'player_of_day' },
        { text: 'Legend of Week', value: 'legend_of_week' },
      ],
      onFilter: (value: unknown, record: DailyDealPurchase) => record.dealType === value,
      render: (text: string) => (
        <Tag color={DEAL_TYPE_COLORS[text] || 'default'} style={{ fontSize: 11 }}>
          {text.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'purchasedAt',
      key: 'purchasedAt',
      width: 160,
      sorter: (a: DailyDealPurchase, b: DailyDealPurchase) =>
        new Date(a.purchasedAt).getTime() - new Date(b.purchasedAt).getTime(),
      defaultSortOrder: 'descend' as const,
      render: (date: string) => dayjs(date).format('DD MMM YYYY, hh:mm A'),
    },
    {
      title: 'Payment',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 110,
      sorter: (a: DailyDealPurchase, b: DailyDealPurchase) =>
        (a.paymentMethod || '').localeCompare(b.paymentMethod || ''),
      filters: [
        { text: 'Free', value: 'free' },
        { text: 'Coins', value: 'coins' },
        { text: 'Match Tokens', value: 'matchTokens' },
        { text: 'Evo Tokens', value: 'evoTokens' },
        { text: 'Gems', value: 'premiumCurrency' },
        { text: 'IAP', value: 'iap' },
      ],
      onFilter: (value: unknown, record: DailyDealPurchase) => record.paymentMethod === value,
      render: (method: string) => (
        <Tag color={PAYMENT_COLORS[method] || 'default'} style={{ fontSize: 11 }}>
          {(method || '').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Cost',
      key: 'cost',
      width: 110,
      sorter: (a: DailyDealPurchase, b: DailyDealPurchase) =>
        (a.currencyCost || a.gemCost || 0) - (b.currencyCost || b.gemCost || 0),
      render: (_: unknown, record: DailyDealPurchase) => {
        if (record.currencyType && record.currencyCost) {
          return (
            <Text strong style={{ color: COST_COLORS[record.currencyType] }}>
              {record.currencyCost.toLocaleString()}{' '}
              <Text type="secondary" style={{ fontSize: 10 }}>
                {formatCurrencyType(record.currencyType)}
              </Text>
            </Text>
          );
        }
        if (record.gemCost) {
          return (
            <Text strong style={{ color: '#eb2f96' }}>
              {record.gemCost}{' '}
              <Text type="secondary" style={{ fontSize: 10 }}>
                Gems
              </Text>
            </Text>
          );
        }
        return <Tag color="green">Free</Tag>;
      },
    },
    {
      title: 'Rewards',
      dataIndex: 'rewardsGranted',
      key: 'rewardsGranted',
      render: renderRewards,
    },
  ];

  const analyticsColumns = [
    {
      title: 'Deal Type',
      dataIndex: 'dealType',
      key: 'dealType',
      render: (text: string) => <Tag color="blue">{text.replace(/_/g, ' ').toUpperCase()}</Tag>,
    },
    { title: 'Total', dataIndex: 'totalClaims', key: 'totalClaims' },
    { title: 'Free', dataIndex: 'freeClaims', key: 'freeClaims' },
    { title: 'Coins', dataIndex: 'coinClaims', key: 'coinClaims' },
    { title: 'MT', dataIndex: 'matchTokenClaims', key: 'matchTokenClaims' },
    { title: 'ET', dataIndex: 'evoTokenClaims', key: 'evoTokenClaims' },
    { title: 'Gems', dataIndex: 'gemClaims', key: 'gemClaims' },
    { title: 'IAP', dataIndex: 'iapClaims', key: 'iapClaims' },
    { title: 'Coins Spent', dataIndex: 'totalCoinsSpent', key: 'totalCoinsSpent' },
    { title: 'MT Spent', dataIndex: 'totalMatchTokensSpent', key: 'totalMatchTokensSpent' },
    { title: 'ET Spent', dataIndex: 'totalEvoTokensSpent', key: 'totalEvoTokensSpent' },
    { title: 'Gems Spent', dataIndex: 'totalGemsSpent', key: 'totalGemsSpent' },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (revenue: number) => formatMoney(revenue, 'INR'),
    },
    { title: 'Users', dataIndex: 'uniqueUsers', key: 'uniqueUsers' },
  ];

  const userDealStatusColumns = [
    {
      title: 'Deal Type',
      dataIndex: 'dealType',
      key: 'dealType',
      render: (text: string) => <Tag color="blue">{text.replace(/_/g, ' ').toUpperCase()}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: UserDealStatus) =>
        record.isOnCooldown ? (
          <Badge
            status="warning"
            text={`Cooldown (${Math.ceil((record.cooldownRemaining || 0) / 60)}m)`}
          />
        ) : (
          <Badge status="success" text="Available" />
        ),
    },
    {
      title: 'Next Available',
      dataIndex: 'nextAvailableAt',
      key: 'nextAvailableAt',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'Now'),
    },
    {
      title: 'Total Claims',
      dataIndex: 'totalClaims',
      key: 'totalClaims',
    },
    {
      title: 'Last Claim',
      dataIndex: 'lastClaimAt',
      key: 'lastClaimAt',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'Never'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: UserDealStatus) => (
        <Button
          type="link"
          size="small"
          icon={<UserDeleteOutlined />}
          onClick={() => {
            resetForm.setFieldsValue({ userId: userStatus?.userId, dealType: record.dealType });
            setResetCooldownModalVisible(true);
          }}
        >
          Reset
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'config',
      label: (
        <span>
          <SettingOutlined />
          Configuration
        </span>
      ),
      children: (
        <>
          <Row justify="end" style={{ marginBottom: 16 }}>
            <Space>
              <Button
                icon={<UserDeleteOutlined />}
                onClick={() => setResetCooldownModalVisible(true)}
              >
                Reset User Cooldown
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => setRegenerateSeedModalVisible(true)}>
                Regenerate Seed
              </Button>
              <Button
                icon={<BarChartOutlined />}
                type="primary"
                onClick={() => handleShowAnalytics()}
              >
                View Analytics
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchConfigs}>
                Refresh
              </Button>
            </Space>
          </Row>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space wrap align="center">
              <Text strong>Viewing deals for:</Text>
              <ConfigVersionSelect
                value={scopeConfigVersionId}
                onChange={setScopeConfigVersionId}
                sharedLabel="All builds (shared)"
              />
              <Text type="secondary">
                {scopeConfigVersionId
                  ? 'Showing what builds on this config version are served \u2014 their own rows where they have one, the shared row otherwise.'
                  : 'Showing the rows every build shares. Pick a config version to vary deals for just those builds.'}
              </Text>
            </Space>
          </Card>
          <Table
            loading={loading}
            dataSource={resolvedConfigs}
            columns={columns}
            rowKey="id"
            pagination={false}
          />
        </>
      ),
    },
    {
      key: 'purchases',
      label: (
        <span>
          <HistoryOutlined />
          Purchase History
        </span>
      ),
      children: (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Form form={purchaseFilterForm} onFinish={handlePurchaseFilter}>
              <Row gutter={[12, 8]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="userId" style={{ marginBottom: 0 }}>
                    <Input placeholder="User ID" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="userName" style={{ marginBottom: 0 }}>
                    <Input placeholder="User Name" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                    <RangePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <Form.Item name="dealType" style={{ marginBottom: 0 }}>
                    <Select placeholder="Deal Type" allowClear>
                      <Option value="free_drop">Free Drop</Option>
                      <Option value="free_coin_drop">Free Coin Drop</Option>
                      <Option value="free_card_drop">Free Card Drop</Option>
                      <Option value="player_of_day">Player of Day</Option>
                      <Option value="legend_of_week">Legend of Week</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row style={{ marginTop: 8 }} justify="space-between">
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    Search
                  </Button>
                  <Button
                    onClick={() => {
                      purchaseFilterForm.resetFields();
                      setPurchasesFilters({});
                      fetchPurchases(1, {});
                    }}
                  >
                    Clear
                  </Button>
                </Space>
                <Button icon={<DownloadOutlined />} onClick={() => handleExport('purchases')}>
                  Export CSV
                </Button>
              </Row>
            </Form>
          </Card>
          <Table
            loading={purchasesLoading}
            dataSource={purchases}
            columns={purchaseColumns}
            rowKey="id"
            size="small"
            scroll={{ x: 900 }}
            pagination={{
              current: purchasesPage,
              total: purchasesTotal,
              pageSize: purchasesPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} purchases`,
              onChange: (page, size) => {
                if (size !== purchasesPageSize) setPurchasesPageSize(size);
                fetchPurchases(page, purchasesFilters, size);
              },
            }}
          />
        </>
      ),
    },
    {
      key: 'user-lookup',
      label: (
        <span>
          <UserOutlined />
          User Lookup
        </span>
      ),
      children: (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Form form={userLookupForm} layout="inline" onFinish={handleUserLookup}>
              <Form.Item name="userId" rules={[{ required: true, message: 'Enter user ID' }]}>
                <Input placeholder="Enter User ID (UUID)" style={{ width: 350 }} />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={userLookupLoading}
                  icon={<SearchOutlined />}
                >
                  Lookup
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {userStatus && (
            <>
              <Card size="small" title="User Information" style={{ marginBottom: 16 }}>
                <Descriptions column={3}>
                  <Descriptions.Item label="User ID">{userStatus.userId}</Descriptions.Item>
                  <Descriptions.Item label="Display Name">
                    {userStatus.userDisplayName || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Claims">
                    {userStatus.totalClaims}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title="Deal Status" style={{ marginBottom: 16 }}>
                <Table
                  dataSource={userStatus.dealStatuses}
                  columns={userDealStatusColumns}
                  rowKey="dealType"
                  pagination={false}
                  size="small"
                />
              </Card>

              <Card size="small" title="Recent Purchases">
                <Table
                  dataSource={userStatus.recentPurchases}
                  columns={[
                    {
                      title: 'Deal Type',
                      dataIndex: 'dealType',
                      render: (t: string) => (
                        <Tag color="blue">{t.replace(/_/g, ' ').toUpperCase()}</Tag>
                      ),
                    },
                    {
                      title: 'Date',
                      dataIndex: 'purchasedAt',
                      render: (d: string) => dayjs(d).format('DD MMM YYYY, hh:mm A'),
                    },
                    {
                      title: 'Payment',
                      dataIndex: 'paymentMethod',
                      render: (m: string) => <Tag>{m.toUpperCase()}</Tag>,
                    },
                    {
                      title: 'Rewards',
                      dataIndex: 'rewardsGranted',
                      render: renderRewards,
                    },
                  ]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>
            <GiftOutlined /> Daily Deals Management
          </Title>
        </Col>
        <Col>
          <Dropdown
            menu={{
              items: exportMenuItems,
              onClick: ({ key }) => handleExport(key as 'purchases' | 'analytics'),
            }}
          >
            <Button icon={<DownloadOutlined />}>Export</Button>
          </Dropdown>
        </Col>
      </Row>

      <Card>
        <Tabs
          items={tabItems}
          onChange={(key) => {
            if (key === 'purchases' && purchases.length === 0) {
              fetchPurchases();
            }
          }}
        />
      </Card>

      {/* Edit Configuration Modal */}
      <Modal
        title={`Edit ${editingConfig?.dealType.replace(/_/g, ' ').toUpperCase()}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditingConfig(null);
          setEditingRewards({});
          setEditingPricing({});
        }}
        onOk={() => editForm.submit()}
        width={700}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
          <Form.Item label="Name" name="name">
            <Input placeholder="Deal name" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Deal description" />
          </Form.Item>

          {/* Multi-Currency Pricing */}
          <Card size="small" title="Multi-Currency Pricing" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Coins">
                  <InputNumber
                    min={0}
                    value={editingPricing.coins}
                    onChange={(v) =>
                      setEditingPricing({ ...editingPricing, coins: v || undefined })
                    }
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Match Tokens">
                  <InputNumber
                    min={0}
                    value={editingPricing.matchTokens}
                    onChange={(v) =>
                      setEditingPricing({ ...editingPricing, matchTokens: v || undefined })
                    }
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Evo Tokens">
                  <InputNumber
                    min={0}
                    value={editingPricing.evoTokens}
                    onChange={(v) =>
                      setEditingPricing({ ...editingPricing, evoTokens: v || undefined })
                    }
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Gems">
                  <InputNumber
                    min={0}
                    value={editingPricing.premiumCurrency}
                    onChange={(v) =>
                      setEditingPricing({ ...editingPricing, premiumCurrency: v || undefined })
                    }
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Text type="secondary">
              Set one or more currencies that can be used to purchase this deal. Leave at 0 if not
              applicable.
            </Text>
          </Card>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Legacy Gem Cost" name="gemCost">
                <InputNumber
                  min={0}
                  placeholder="Legacy (use pricing above)"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="IAP Cost (USD)" name="iapCostUsd">
                <InputNumber min={0} step={0.01} placeholder="IAP cost" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="IAP Product ID" name="iapProductId">
            <Input placeholder="Store product ID" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              {/*
                Cooldown is enforced globally per deal type: `user_daily_deals`
                is keyed on (userId, dealType) with no config version, so a
                variant with a shorter window would hand a player a second free
                claim just by switching builds. The server refuses the edit —
                disable it here rather than let someone type a value and get a
                422 back.
              */}
              <Form.Item
                label="Cooldown Hours"
                name="cooldownHours"
                tooltip={
                  editingConfig?.configVersionId
                    ? 'Cooldown is the same for every build. Edit it on the shared deal and it applies to all overrides.'
                    : undefined
                }
                extra={
                  editingConfig?.configVersionId
                    ? 'Set on the shared deal \u2014 applies to every build.'
                    : undefined
                }
              >
                <InputNumber
                  min={1}
                  placeholder="Cooldown in hours"
                  style={{ width: '100%' }}
                  disabled={Boolean(editingConfig?.configVersionId)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Sort Order" name="sortOrder">
                <InputNumber min={0} placeholder="Display order" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Featured Player Index" name="featuredPlayerIndex">
                <InputNumber
                  min={0}
                  max={maxPlayerIndex}
                  placeholder="Auto"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          {editingConfig && (
            <Form.Item label="Rewards Configuration">
              <RewardsEditor
                dealType={editingConfig.dealType}
                rewards={editingRewards}
                onChange={setEditingRewards}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Reset Cooldown Modal */}
      <Modal
        title="Reset User Cooldown"
        open={resetCooldownModalVisible}
        onCancel={() => {
          setResetCooldownModalVisible(false);
          resetForm.resetFields();
        }}
        onOk={() => resetForm.submit()}
      >
        <Form form={resetForm} layout="vertical" onFinish={handleResetCooldown}>
          <Form.Item
            label="User ID"
            name="userId"
            rules={[{ required: true, message: 'Please enter user ID' }]}
          >
            <Input placeholder="Enter user UUID" />
          </Form.Item>
          <Form.Item
            label="Deal Type"
            name="dealType"
            rules={[{ required: true, message: 'Please select deal type' }]}
          >
            <Select placeholder="Select deal type">
              <Option value="free_drop">Free Drop</Option>
              <Option value="free_coin_drop">Free Coin Drop</Option>
              <Option value="free_card_drop">Free Card Drop</Option>
              <Option value="player_of_day">Player of Day</Option>
              <Option value="legend_of_week">Legend of Week</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Regenerate Seed Modal */}
      <Modal
        title="Regenerate Daily Drop Seed"
        open={regenerateSeedModalVisible}
        onCancel={() => {
          setRegenerateSeedModalVisible(false);
          regenerateForm.resetFields();
        }}
        onOk={() => regenerateForm.submit()}
      >
        <Form form={regenerateForm} layout="vertical" onFinish={handleRegenerateSeed}>
          <Form.Item label="Date (Optional)" name="date">
            <DatePicker style={{ width: '100%' }} placeholder="Leave empty for today" />
          </Form.Item>
          <Text type="secondary">
            Leave date empty to regenerate today's seed. All users will get the new drop after
            regeneration.
          </Text>
        </Form>
      </Modal>

      {/* Analytics Modal */}
      <Modal
        title="Daily Deals Analytics"
        open={analyticsModalVisible}
        onCancel={() => setAnalyticsModalVisible(false)}
        footer={[
          <Button
            key="export"
            icon={<DownloadOutlined />}
            onClick={() => handleExport('analytics')}
          >
            Export CSV
          </Button>,
          <Button key="close" type="primary" onClick={() => setAnalyticsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width="90%"
        centered
      >
        {/* Filters */}
        <Form
          form={analyticsForm}
          layout="inline"
          onFinish={handleShowAnalytics}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="startDate">
            <DatePicker placeholder="Start date" />
          </Form.Item>
          <Form.Item name="endDate">
            <DatePicker placeholder="End date" />
          </Form.Item>
          <Form.Item name="dealType">
            <Select placeholder="Deal type" style={{ width: 170 }} allowClear>
              <Option value="free_drop">Free Drop</Option>
              <Option value="free_coin_drop">Free Coin Drop</Option>
              <Option value="free_card_drop">Free Card Drop</Option>
              <Option value="player_of_day">Player of Day</Option>
              <Option value="legend_of_week">Legend of Week</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Filter
            </Button>
          </Form.Item>
        </Form>

        {/* Summary Cards */}
        {analytics.length > 0 && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Total Claims"
                    value={analytics.reduce((s, a) => s + a.totalClaims, 0)}
                    prefix={<GiftOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Total Revenue"
                    value={analytics.reduce((s, a) => s + a.totalRevenue, 0)}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Unique Users"
                    value={Math.max(...analytics.map((a) => a.uniqueUsers), 0)}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic
                    title="Total Currency Spent"
                    value={analytics.reduce(
                      (s, a) =>
                        s +
                        a.totalCoinsSpent +
                        a.totalMatchTokensSpent +
                        a.totalEvoTokensSpent +
                        a.totalGemsSpent,
                      0
                    )}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Per-Deal Breakdown Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {analytics.map((deal) => {
                const totalPaid =
                  deal.coinClaims +
                  deal.matchTokenClaims +
                  deal.evoTokenClaims +
                  deal.gemClaims +
                  deal.iapClaims;
                const isLow = deal.dealType === 'legend_of_week';
                return (
                  <Col xs={24} lg={12} key={deal.dealType}>
                    <Card
                      size="small"
                      title={
                        <Space>
                          {isLow ? (
                            <CrownOutlined style={{ color: '#faad14' }} />
                          ) : (
                            <GiftOutlined />
                          )}
                          <span>{deal.dealType.replace(/_/g, ' ').toUpperCase()}</span>
                          <Tag>{deal.totalClaims} claims</Tag>
                        </Space>
                      }
                    >
                      {/* Payment Method Breakdown */}
                      <div style={{ marginBottom: 12 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Payment Methods
                        </Text>
                        <div style={{ marginTop: 4 }}>
                          {deal.freeClaims > 0 && <Tag color="green">Free: {deal.freeClaims}</Tag>}
                          {deal.coinClaims > 0 && <Tag color="gold">Coins: {deal.coinClaims}</Tag>}
                          {deal.matchTokenClaims > 0 && (
                            <Tag color="purple">MT: {deal.matchTokenClaims}</Tag>
                          )}
                          {deal.evoTokenClaims > 0 && (
                            <Tag color="cyan">ET: {deal.evoTokenClaims}</Tag>
                          )}
                          {deal.gemClaims > 0 && <Tag color="magenta">Gems: {deal.gemClaims}</Tag>}
                          {deal.iapClaims > 0 && <Tag color="red">IAP: {deal.iapClaims}</Tag>}
                          {deal.totalClaims === 0 && <Text type="secondary">No claims</Text>}
                        </div>
                      </div>

                      {/* Currency Spent */}
                      <Row gutter={8}>
                        <Col span={6}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Coins
                          </Text>
                          <div>{deal.totalCoinsSpent.toLocaleString()}</div>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            MT
                          </Text>
                          <div>{deal.totalMatchTokensSpent.toLocaleString()}</div>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ET
                          </Text>
                          <div>{deal.totalEvoTokensSpent.toLocaleString()}</div>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Gems
                          </Text>
                          <div>{deal.totalGemsSpent.toLocaleString()}</div>
                        </Col>
                      </Row>

                      {/* Revenue & Users */}
                      <div
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px solid #f0f0f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text>
                          Revenue:{' '}
                          <Text strong style={{ color: '#3f8600' }}>
                            {formatMoney(deal.totalRevenue, 'INR')}
                          </Text>
                        </Text>
                        <Text>
                          Users: <Text strong>{deal.uniqueUsers}</Text>
                        </Text>
                        {deal.totalClaims > 0 && (
                          <Text>
                            Paid:{' '}
                            <Text strong>
                              {deal.totalClaims > 0
                                ? ((totalPaid / deal.totalClaims) * 100).toFixed(0)
                                : 0}
                              %
                            </Text>
                          </Text>
                        )}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            <Divider />

            {/* Raw Data Table */}
            <Table
              dataSource={analytics}
              columns={analyticsColumns}
              rowKey="dealType"
              pagination={false}
              size="small"
              scroll={{ x: 1200 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};
